import { useEffect } from 'react'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useQueryClient } from 'react-query'
import { getUniqueIdentifier } from 'utils/sessionStorage'
import { workspaceTasksKeys } from 'screens/Todos/hooks/useWorkspaceTasks'
import { useFilters } from 'screens/Todos/hooks/useFilters'

const CHANNEL_NAME = 'ReviewsTodosChannel'

interface Payload {
  operation: string
  session_uuid: string | undefined | null
  article_id?: string
  request_id?: string
}

export const useReviewsTodosChannel = () => {
  const websocketConnection = useWebsocketConnection()
  const queryClient = useQueryClient()
  const auth = useAuth()
  const { values: filters } = useFilters()
  const currentWorkspace = useCurrentWorkspace()
  const currentSessionUuid = getUniqueIdentifier()

  useEffect(() => {
    if (!websocketConnection || !auth || !currentWorkspace) return

    const subscriptionParams = {
      channel: CHANNEL_NAME,
      user_id: auth.id,
      workspace_id: currentWorkspace.id,
    }
    const subscription = websocketConnection.subscriptions.create(subscriptionParams, {
      received: ({ operation, article_id, request_id, session_uuid }: Payload) => {
        queryClient.invalidateQueries(['workspaces', currentWorkspace.id, 'todosMetrics'])
        queryClient.invalidateQueries(workspaceTasksKeys.tasksAll(currentWorkspace.key))

        const isSameSession = session_uuid && session_uuid === currentSessionUuid
        if (isSameSession) return null

        if (article_id || request_id) {
          queryClient.invalidateQueries(['articles', article_id, 'tasks', filters, request_id || false])
        }

        if (
          ['review_submitted', 'review_dismissed', 'request_closed', 'request_created', 'review_created'].includes(
            operation,
          )
        ) {
          queryClient.invalidateQueries(workspaceTasksKeys.forType(currentWorkspace.key, filters, 'standard'))
          queryClient.invalidateQueries(workspaceTasksKeys.forType(currentWorkspace.key, filters, 'approvals'))
        }

        if (['todo_completed', 'todo_created', 'todo_deleted', 'todo_incompleted'].includes(operation)) {
          queryClient.invalidateQueries(workspaceTasksKeys.forType(currentWorkspace.key, filters, 'todos'))
        }

        if (['marked_as_read', 'viewer_removed', 'viewer_created'].includes(operation)) {
          queryClient.invalidateQueries(workspaceTasksKeys.forType(currentWorkspace.key, filters, 'read_receipts'))
        }

        if (['review_submitted', 'request_closed'].includes(operation)) {
          queryClient.invalidateQueries(workspaceTasksKeys.recentlyClosed(currentWorkspace.key, filters))
        }

        if (['review_submitted', 'review_dismissed'].includes(operation)) {
          queryClient.invalidateQueries(workspaceTasksKeys.completed(currentWorkspace.key, filters))
        }
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [websocketConnection, auth, currentWorkspace, queryClient, currentSessionUuid, filters])
}
